import React, { FC, useCallback, MouseEvent } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { makeStyles } from '@mui/styles'
import {
  Box,
  Checkbox,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Theme } from '@mui/material/styles'

import { RiskScoreEnum, TransactionStatusEnum, TransactionTypeEnum } from '@sparling/financial-common'
import { IColumns, IDataItem } from './TableTypes'
import { icons } from '../../constants/icons'
import { KycStatus } from '../../pages/Users/usersList'
import { TradingStatus } from '../../pages/Trading/tradingList'
import { RiskLevel } from '../../pages/Settings/Assets/Staking/stakingList'
import { routes } from '../../constants/routes'
import { Loader } from '../Loader'
import { AddButton } from '../Buttons/AddButton'
import { DoubleActions } from '../DoubleActions'
import { TableCheckControl } from '../TableCheckControl'
import { GreenButton } from '../Buttons/GreenButton'
import { TableClientTypesRow, ClientType } from '../TableClientTypesRow'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    margin: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& thead tr': {
      height: '60px',
    },
    '& th': {
      color: theme.palette.primary.light,
      opacity: '0.6',
      fontWeight: 400,
      borderColor: 'rgba(233,236,239,0.38)',
    },
    '& tr': {
      height: '80px',
      '&:nth-child(even)': {
        backgroundColor: 'rgba(216,216,216,0.05)',
      },
    },
    '& td': {
      color: theme.palette.primary.light,
      fontSize: 16,
      border: 'none',
      // padding: 2,
      '&.grey': {
        color: '#A5A5A5',
      },
      '&.green': {
        color: 'green',
      },
      '&.red': {
        color: 'red',
      },
      '&.orange': {
        color: 'orange',
      },
      '&.blue': {
        color: 'blue',
      },
      '& .MuiCheckbox-root': {
        pointerEvents: 'none',
      },
    },
  },
  container: {
    maxHeight: '1000px',
  },
  pagination: {
    marginTop: '24px',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    '& .MuiPaginationItem-root': {
      color: '#A9AAAD',
      fontSize: '0.75rem',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    '& .MuiPaginationItem-page.Mui-selected': {
      color: '#51e5a5',
      textAlign: 'center',
      backgroundColor: '#2d3f3d',
      border: '1px solid #51e5a5',
      borderRadius: '4px',
    },
    '& .MuiPagination-ul': {
      flexWrap: 'unset',
    },
  },
  editIconGreen: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.secondary.light,
    },
  },
  editIconDisabled: {
    color: '#a8a9ac',
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  editIconGray: {
    color: '#a8a9ac',
    paddingLeft: 0,
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  infoIcon: {
    color: '#a8a9ac',
    paddingLeft: 0,
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  errorIcon: {
    color: '#a8a9ac',
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  deleteIcon: {
    color: '#A9544B',
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  tableTh: {
    textTransform: 'uppercase',
  },
  iconRow: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
  iconCopy: {
    padding: 0,
    color: theme.palette.secondary.main,
    opacity: 0.6,
    paddingLeft: theme.spacing(1),
    '& :first-child': {
      padding: 0,
      fontSize: 18,
    },
  },
  pointer: {
    cursor: 'pointer',
  },
  notFound: {
    backgroundColor: 'transparent !important',
    '& td': {
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(6),
    },
  },
  tableRowHover: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08) !important',
    },
  },
}))

interface TableProps {
  columns: IColumns[]
  data: IDataItem[]
  loadData?: (value: number) => void
  page?: string | number
  getElementId?: (value: string | number, idSf?: string) => void
  onAdd?: (value: number) => void
  onInfo?: (value?: string | number) => void
  onRemove?: (value: number) => void
  onEdit?: (value: string | number) => void
  onDelete?: (id: string | number) => void
  onCheck?: (event: MouseEvent<HTMLButtonElement>, page: number) => void
  onCheckAll?: (page: number) => void
  checkboxIds?: Record<number, number[]>
  twoAction?: true
  threeAction?: true
  isShowDoubleActionBtn?: boolean
  isShowActionBtn?: boolean
  isShowDetailsBtn?: boolean
  isShowRemoveIcon?: boolean
  isShowInfoIcon?: boolean
  isShowEditIcon?: boolean
  loading?: boolean
  isActive?: boolean
  checkedIds?: number[]
  params: {
    currentPage: number
    itemsPerPage: number
    itemCount: number
    totalItems: number
    totalPages: number
  }
}

export const TableComponent: FC<TableProps> = ({
  columns,
  data,
  loadData,
  getElementId,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onAdd = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onInfo = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onRemove = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onEdit = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDelete,
  onCheck = () => void 0,
  onCheckAll = () => void 0,
  checkboxIds,
  params,
  isShowDoubleActionBtn = false,
  isShowActionBtn = false,
  isShowDetailsBtn = false,
  isShowRemoveIcon = false,
  isShowInfoIcon = true,
  isShowEditIcon = true,
  loading,
  checkedIds,
}) => {
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()

  const searchParams = new URLSearchParams(location.search)
  const currentPage = Number(searchParams.get('page') || params?.currentPage || 1)

  // eslint-disable-next-line
  const paginate = (event: any, pageNumber: any) => {
    // eslint-disable-next-line no-unused-expressions
    loadData && loadData(pageNumber)
    searchParams.set('page', pageNumber)
    history.push(`${location.pathname}?${searchParams.toString()}`)
  }

  const copyClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast('Copied to clipboard!', { type: 'success' })
    })
  }, [])

  // eslint-disable-next-line
  const renderComponent = (row: IDataItem, column: IColumns, rowData: any) => {
    const defaultValue = '-'
    if (typeof rowData === 'boolean') {
      if (rowData === true) {
        return <Checkbox defaultChecked color="success" />
      }
      return <Checkbox disabled />
    }

    if (column.format) {
      return column.format?.(rowData, row?.owner, row?.type, row?.riskScore, row) || defaultValue
    }
    return rowData || defaultValue
  }

  const renderCell = useCallback(
    (index: number, row: IDataItem, column: IColumns) => {
      switch (column.id) {
        case 'actions':
          return (
            <Box key={`action-${index}`} className={classes.iconRow}>
              {isShowDoubleActionBtn && <DoubleActions id={row.id} status={row?.status} />}
              {isShowActionBtn && (
                <AddButton width={64} text={'ADD'} onClick={() => onAdd(row.id)} added={checkedIds?.includes(row.id)} />
              )}
              {isShowDetailsBtn && <GreenButton text="Details" width={120} href={`/users/${row?.idSf}/details`} />}
              {isShowRemoveIcon && (
                <IconButton
                  className={classes.infoIcon}
                  onClick={(event) => {
                    event.stopPropagation()
                    onRemove(row.id)
                  }}
                >
                  <span className="material-icons">{icons.REMOVE}</span>
                </IconButton>
              )}
              {isShowInfoIcon && (
                <IconButton
                  className={classes.infoIcon}
                  onClick={(event) => {
                    event.stopPropagation()
                    onInfo(row.id)
                  }}
                >
                  <span className="material-icons">{icons.VISIBILITY}</span>
                </IconButton>
              )}

              {isShowEditIcon && (
                <IconButton
                  className={classes.editIconGreen}
                  onClick={(event) => {
                    event.stopPropagation()
                    onEdit(row.id)
                  }}
                >
                  <span className="material-icons">{icons.EDIT}</span>
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  className={classes.deleteIcon}
                  onClick={(event) => {
                    event.stopPropagation()
                    onDelete(row.id)
                  }}
                >
                  <span className="material-icons">{icons.DELETE}</span>
                </IconButton>
              )}
            </Box>
          )
        case 'address':
          return (
            <Box key={`address-${index}`} className={classes.iconRow}>
              <Box>{renderComponent(row, column, row[column.id])}</Box>
              <Box>
                <IconButton className={classes.iconCopy} onClick={() => copyClipboard(String(row[column.id]))}>
                  <span className="material-icons">{icons.COPY}</span>
                </IconButton>
              </Box>
            </Box>
          )
        case 'treeAddress':
          return (
            <Box key={`three-action-${index}`} className={classes.iconRow}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {!(row.status === TransactionStatusEnum.COMPLETED || row.status === TransactionStatusEnum.REJECTED) && (
                <IconButton className={classes.editIconGray} onClick={() => onEdit(row.id)}>
                  <span className="material-icons">{icons.EDIT}</span>
                </IconButton>
              )}

              <IconButton className={classes.errorIcon}>
                <span className="material-icons">{icons.ERROR}</span>
              </IconButton>

              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <IconButton className={classes.deleteIcon} onClick={() => onDelete(row.id)}>
                <span className="material-icons">{icons.DELETE}</span>
              </IconButton>
            </Box>
          )
        case 'clientTypes':
          return (
            <Box key={`clientTypes-${index}`} className={classes.iconRow}>
              <TableClientTypesRow
                assetId={row.id}
                clientTypes={row.clientTypes as ClientType[]}
                fireblockBaseAssetsId={row.fireblockBaseAssetsId as string}
                fireblockQuoteAssetsId={row.fireblockQuoteAssetsId as string}
              />
            </Box>
          )
        case 'checkbox':
          return (
            <Box key={`checkbox${index}`} className={classes.iconRow}>
              <TableCheckControl id={row.id} page={params?.currentPage} checkboxIds={checkboxIds} onCheck={onCheck} />
            </Box>
          )
        default:
          return (
            <Box key={`${column.id}-${Math.random()}`} textAlign="left">
              {renderComponent(row, column, row[column.id])}
            </Box>
          )
      }
    },
    [
      classes,
      isShowActionBtn,
      isShowRemoveIcon,
      isShowEditIcon,
      isShowInfoIcon,
      isShowDetailsBtn,
      onAdd,
      onInfo,
      onRemove,
      onDelete,
      onEdit,
      onCheck,
      checkboxIds,
      params?.currentPage,
      isShowDoubleActionBtn,
      copyClipboard,
      checkedIds,
    ],
  )

  const setColor = useCallback(
    (index: number, row: IDataItem) => {
      let color = ''
      switch (true) {
        case (index === 5 && (row.kycStatus === KycStatus.Failed || row.kycStatus === KycStatus.Rejected)) ||
          (location.pathname === routes.TRADING
            ? index === 13 && row.status === TradingStatus.Cancelled
            : index === 12 && row.status === TradingStatus.Cancelled) ||
          (index === 4 && row.riskLevel === RiskLevel.High) ||
          (location.pathname === routes.FINANCE
            ? (index === 5 && row.riskScore === RiskScoreEnum.HIGH) ||
              (index === 5 && row.riskScore === RiskScoreEnum.SEVERE) ||
              (index === 6 && row.status === TransactionStatusEnum.REJECTED)
            : (index === 4 && row.riskScore === RiskScoreEnum.HIGH) ||
              (index === 4 && row.riskScore === RiskScoreEnum.SEVERE) ||
              (index === 5 && row.status === TransactionStatusEnum.REJECTED)):
          color = 'red'
          break

        case (index === 5 && row.kycStatus === KycStatus.InProcess) ||
          (index === 5 && row.kycStatus === KycStatus.Requested) ||
          (location.pathname === routes.TRADING
            ? index === 13 && row.status === TradingStatus.Open
            : index === 12 && row.status === TradingStatus.Open) ||
          (index === 4 && row.riskLevel === RiskLevel.Medium) ||
          (location.pathname === routes.FINANCE
            ? (index === 5 && row.riskScore === RiskScoreEnum.MEDIUM) ||
              (index === 6 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.riskScore === RiskScoreEnum.MEDIUM &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              (index === 6 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.riskScore === RiskScoreEnum.HIGH &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              (index === 6 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.riskScore === RiskScoreEnum.SEVERE &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              // (index === 6 && row.status === TransactionStatusEnum.ACTION_NEEDED) ||
              // (index === 6 && row.status === TransactionStatusEnum.NEW) ||
              (index === 6 && row.status === TransactionStatusEnum.INITIATED)
            : // (index === 6 && row.status === TransactionStatusEnum.UNDER_REVIEW)
              (index === 4 && row.riskScore === RiskScoreEnum.MEDIUM) ||
              (index === 5 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.riskScore === RiskScoreEnum.MEDIUM &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              (index === 5 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.riskScore === RiskScoreEnum.HIGH &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              (index === 5 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.riskScore === RiskScoreEnum.SEVERE &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              // (index === 5 && row.status === TransactionStatusEnum.ACTION_NEEDED) ||
              // (index === 5 && row.status === TransactionStatusEnum.NEW) ||
              (index === 5 && row.status === TransactionStatusEnum.INITIATED)):
          // (index === 5 && row.status === TransactionStatusEnum.UNDER_REVIEW)):
          color = 'orange'
          break

        case (index === 5 && row.kycStatus === KycStatus.Passed) ||
          (location.pathname === routes.TRADING
            ? index === 13 && row.status === TradingStatus.Executed
            : index === 12 && row.status === TradingStatus.Executed) ||
          (index === 4 && row.riskLevel === RiskLevel.Low) ||
          (location.pathname === routes.FINANCE
            ? (index === 5 && row.riskScore === RiskScoreEnum.LOW) ||
              (index === 6 &&
                // row.status === TransactionStatusEnum.SIGNED &&
                row.type === TransactionTypeEnum.DEPOSIT) ||
              (index === 6 && row.status === TransactionStatusEnum.COMPLETED)
            : // (index === 6 && row.status === TransactionStatusEnum.APPROVED)
              index === 4 && row.riskScore === RiskScoreEnum.LOW) ||
          // (index === 5 && row.status === TransactionStatusEnum.SIGNED && row.type === TransactionTypeEnum.DEPOSIT) ||
          (index === 5 && row.status === TransactionStatusEnum.COMPLETED):
          color = 'green'
          break

        case index === 5 && row.kycStatus === KycStatus.NotInitial:
          color = 'grey'
          break

        default:
          color = '#FFFFFF'
      }
      return color
    },
    [location],
  )

  return (
    <>
      {loading && <Loader />}

      <Paper className={classes.root}>
        <TableContainer>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={`${column.id}-${Math.random()}`} align="left" className={classes.tableTh}>
                    {column.id === 'checkbox' ? (
                      <TableCheckControl
                        page={params?.currentPage}
                        checkboxIds={checkboxIds}
                        totalItems={data?.length}
                        onCheckAll={onCheckAll}
                        isHeader
                      />
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* eslint-disable-next-line no-nested-ternary */}
              {data && data.length > 0 ? (
                data.map((row) => {
                  return (
                    <TableRow
                      className={location.pathname === routes.USERS || routes.COMPANIES ? classes.pointer : ''}
                      hover
                      role="checkbox"
                      key={`${row.id}-${Math.random()}`}
                      onClick={() => (getElementId ? getElementId(row.id) : null)}
                    >
                      {columns.map((column: IColumns, index: number) => (
                        <TableCell key={`${index}-${Math.random()}`} className={setColor(index, row)}>
                          {renderCell(index, row, column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              ) : (
                <TableRow className={classes.notFound}>
                  <TableCell colSpan={columns.length} align={'center'}>
                    {!loading && 'No results found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {params && params.totalPages > 1 && (
          <Pagination
            className={classes.pagination}
            count={params.totalPages}
            defaultPage={params.currentPage}
            shape="rounded"
            onChange={paginate}
            page={currentPage}
          />
        )}
      </Paper>
    </>
  )
}
